'use client';

import { useState, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';

const Pie = dynamic(() => import('react-chartjs-2').then((mod) => mod.Pie), { ssr: false });

let chartJsRegistration;

function ensureChartJsRegistered() {
    if (!chartJsRegistration) {
        chartJsRegistration = import('chart.js').then((chartModule) => {
            const { Chart, PieController, ArcElement, Tooltip, Legend } = chartModule;
            Chart.register(PieController, ArcElement, Tooltip, Legend);
            return true;
        });
    }

    return chartJsRegistration;
}

export function PieChart({
    data,
    height = 400,
    width = 400,
    title,
    description,
    legendPosition = 'top',
    hideTitle = false
}) {
    const [isChartReady, setIsChartReady] = useState(false);
    const [isChartJsLoaded, setIsChartJsLoaded] = useState(false);
    const chartRef = useRef(null);

    useEffect(() => {
        let isMounted = true;

        ensureChartJsRegistered().then(() => {
            if (isMounted) {
                setIsChartJsLoaded(true);
            }
        });

        const checkDataReady = () => {
            if (data && data.datasets && data.datasets.length > 0) {
                const hasValidData = data.datasets.some(
                    (dataset) => dataset.data && dataset.data.length > 0
                );
                setIsChartReady(hasValidData);
            } else {
                setIsChartReady(false);
            }
        };

        checkDataReady();

        return () => {
            isMounted = false;
        };
    }, [data]);

    const handleAnimationComplete = () => {
        setIsChartReady(true);
    };

    return (
        <div className="rounded-lg bg-white p-6 shadow-md">
            <div className="mx-auto">
                {!hideTitle && title && (
                    <h2 className="mb-5 text-center text-xl font-bold text-[#0A3B66] sm:text-2xl">
                        {title}
                    </h2>
                )}
                {description && (
                    <p
                        className="mb-4 text-center text-gray-600"
                        dangerouslySetInnerHTML={{ __html: description }}></p>
                )}
                <div className="mx-auto" style={{ maxWidth: width + 'px', height: height + 'px' }}>
                    {isChartReady && isChartJsLoaded && data && (
                        <Pie
                            ref={chartRef}
                            data={data}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                animation: {
                                    onComplete: handleAnimationComplete
                                },
                                plugins: {
                                    legend: {
                                        position: legendPosition
                                    },
                                    title: {
                                        display: false
                                    }
                                }
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
